import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";

export default function OfferBuilder() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const candidateId = params.get("candidateId") || "";
  const jobId = params.get("jobId") || "";

  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    candidateId, jobId, templateId: "",
    salaryAmount: "", salaryCurrency: "INR", salaryFrequency: "ANNUAL",
    startDate: "", reportingManager: "", department: "", location: "",
    probationMonths: 3, benefitsText: "", customTerms: "",
  });
  const [previewHtml, setPreviewHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    document.title = "Build offer - Ample Hire";
    axiosInstance.get(`${baseUrl}/job-posting/offers/templates`)
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setTemplates(list);
        if (list[0]) {
          setForm((f) => ({ ...f, templateId: list[0].id }));
          setPreviewHtml(list[0].bodyHtml || "");
        }
      });
  }, []);

  // Live preview: render selected template with form variables (client-side preview)
  useEffect(() => {
    const t = templates.find((x) => x.id === Number(form.templateId));
    if (!t) return;
    let html = t.bodyHtml || "";
    const vars = {
      candidateName: `Candidate #${form.candidateId || "?"}`,
      jobTitle: `Job #${form.jobId || "?"}`,
      companyName: "Ample Hire",
      department: form.department,
      location: form.location,
      startDate: form.startDate || "TBD",
      reportingManager: form.reportingManager,
      salary: form.salaryAmount ? `${form.salaryCurrency} ${form.salaryAmount} / ${form.salaryFrequency}` : "TBD",
      probation: form.probationMonths,
      benefits: form.benefitsText,
      customTerms: form.customTerms,
    };
    Object.entries(vars).forEach(([k, v]) => {
      html = html.split(`{{${k}}}`).join(v ?? "");
    });
    html = html.replace(/\{\{[^}]+\}\}/g, "");
    setPreviewHtml(html);
  }, [form, templates]);

  const create = async (sendNow = false) => {
    setBusy(true);
    try {
      const r = await axiosInstance.post(`${baseUrl}/job-posting/offers`, {
        ...form,
        candidateId: Number(form.candidateId),
        jobId: Number(form.jobId),
        templateId: form.templateId ? Number(form.templateId) : null,
        salaryAmount: form.salaryAmount ? Number(form.salaryAmount) : null,
        startDate: form.startDate || null,
        probationMonths: Number(form.probationMonths),
      });
      if (sendNow) {
        const sendResp = await axiosInstance.post(`${baseUrl}/job-posting/offers/${r.data.id}/send`);
        setDone({ ...sendResp.data, sent: true });
      } else {
        setDone({ id: r.data.id, status: "DRAFT" });
      }
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="page-enter max-w-2xl mx-auto py-12 text-center">
        <div className="text-5xl mb-3">✓</div>
        <h2 className="text-xl font-semibold text-vf-base">{done.sent ? "Offer sent" : "Offer saved as draft"}</h2>
        {done.candidateLink && (
          <p className="text-sm text-vf-muted mt-2">
            Candidate link: <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">{done.candidateLink}</code>
          </p>
        )}
        <button onClick={() => nav("/admin/offers")} className="mt-5 px-4 py-2 rounded bg-vf-primary text-white text-sm">
          View all offers
        </button>
      </div>
    );
  }

  return (
    <div className="page-enter">
      <div className="flex items-end justify-between mb-5">
        <div>
          <button onClick={() => nav(-1)} className="text-xs text-vf-muted hover:text-vf-base mb-1">← Back</button>
          <h1 className="text-2xl font-semibold text-vf-base">Build offer</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Candidate ID *">
              <input value={form.candidateId} onChange={(e) => setForm({...form, candidateId: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
            </Field>
            <Field label="Job ID *">
              <input value={form.jobId} onChange={(e) => setForm({...form, jobId: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
            </Field>
          </div>
          <Field label="Template">
            <select value={form.templateId} onChange={(e) => setForm({...form, templateId: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm">
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Salary">
              <input type="number" value={form.salaryAmount} onChange={(e) => setForm({...form, salaryAmount: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
            </Field>
            <Field label="Currency">
              <select value={form.salaryCurrency} onChange={(e) => setForm({...form, salaryCurrency: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm">
                <option>INR</option><option>USD</option><option>EUR</option><option>GBP</option>
              </select>
            </Field>
            <Field label="Frequency">
              <select value={form.salaryFrequency} onChange={(e) => setForm({...form, salaryFrequency: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm">
                <option>ANNUAL</option><option>MONTHLY</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date">
              <input type="date" value={form.startDate} onChange={(e) => setForm({...form, startDate: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
            </Field>
            <Field label="Probation (months)">
              <input type="number" value={form.probationMonths} onChange={(e) => setForm({...form, probationMonths: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
            </Field>
          </div>
          <Field label="Reporting manager">
            <input value={form.reportingManager} onChange={(e) => setForm({...form, reportingManager: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Department"><input value={form.department} onChange={(e) => setForm({...form, department: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" /></Field>
            <Field label="Location"><input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" /></Field>
          </div>
          <Field label="Benefits">
            <textarea value={form.benefitsText} onChange={(e) => setForm({...form, benefitsText: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm resize-none" rows={3} />
          </Field>
          <Field label="Custom terms">
            <textarea value={form.customTerms} onChange={(e) => setForm({...form, customTerms: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm resize-none" rows={3} />
          </Field>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <button onClick={() => create(false)} disabled={busy} className="px-3 py-1.5 rounded border border-slate-300 text-sm">Save draft</button>
            <button onClick={() => create(true)} disabled={busy} className="px-3 py-1.5 rounded bg-vf-primary text-white text-sm">Send to candidate</button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="text-xs font-semibold text-vf-muted uppercase mb-3">Live preview</div>
          <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-vf-muted mb-0.5">{label}</div>
      {children}
    </label>
  );
}
